<script setup lang="ts">
import { useI18n } from 'vue-i18n';
import { useDark } from '@vueuse/core';
import { useCoreStore } from '@/stores/core/store';
import { computed, PropType, ref, toRefs } from 'vue';
import useButtonPreset from '@/presets/customizations/button';

const emit = defineEmits(['click']);

const props = defineProps({
  permission: {
    type: String,
  },
  icon: {
    type: String,
  },
  iconImg: {
    type: String,
  },
  iconImgDark: {
    type: String,
  },
  label: {
    type: String,
  },
  tooltip: {
    type: String,
  },
  hideBorders: {
    type: Boolean,
  },
  disabled: {
    type: Boolean,
  },
  useFlat: {
    type: Boolean,
    default: false,
  },
  outlined: {
    type: Boolean,
    default: true,
  },
  rounded: {
    type: Boolean,
    default: true,
  },
  roundedFull: {
    type: Boolean,
    default: false,
  },
  minWidth: {
    type: Number,
  },
  severity: {
    type: String as PropType<
      'primary' | 'secondary' | 'regular' | 'success' | 'info' | 'warning' | 'help' | 'danger' | 'contrast' | 'delete' | undefined
    >,
  },
  useRightPadding: {
    type: Boolean,
  },
  useContrast: {
    type: Boolean,
  },
  iconPos: {
    type: String as PropType<'left' | 'right' | 'top' | 'bottom'>,
    default: 'left',
  },
  iconClass: {
    type: String,
    default: '',
  },
  useIconify: {
    type: Boolean,
    default: true,
  },
  size: {
    type: String as PropType<'small' | 'large' | undefined>,
    default: 'large',
  },
  cssClass: {
    type: String,
    default: '',
  },
  reverseIconDirection: {
    type: Boolean,
  },
  isFlat: {
    type: Boolean,
    default: false,
  },
});

const { t } = useI18n();
const buttonRef = ref();
const isDark = useDark();
const propsRef = toRefs(props);
const coreStore = useCoreStore();
const btnPresent = useButtonPreset(propsRef.severity, propsRef.rounded, propsRef.roundedFull, propsRef.isFlat);

const style = computed(() => {
  return {
    minWidth: props.minWidth ? props.minWidth + 'px' : 'unset',
  };
});

const onClick = (event: any) => {
  emit('click', event);
  buttonRef.value.$el.blur();
};

const triggerClick = () => {
  buttonRef.value.$el.click();
};

const iconImgComp = computed(() => (isDark.value ? props.iconImgDark || props.iconImg : props.iconImg));

const hasPermission = computed(() => {
  return props.permission ? coreStore.hasPermission(props.permission).value : true;
});

const isDisabled = computed(() => {
  return props.disabled || !hasPermission.value;
});

defineExpose({
  triggerClick,
});
</script>

<template>
  <div
    class="inline-block"
    :class="{ '!cursor-not-allowed': isDisabled, 'w-full': cssClass && cssClass.includes('w-full') }"
    v-tooltip.bottom="{
      value: !hasPermission ? t('core.notAllowedDueMissingPermission') : tooltip ? $t(tooltip) : undefined,
      showDelay: 500,
    }"
  >
    <Button
      variant="text"
      :size="size"
      :severity="severity"
      :icon="icon"
      :disabled="isDisabled"
      @click="onClick"
      :outlined="outlined"
      :style="style"
      :text="useFlat && !outlined"
      :label="label ? $t(label) : undefined"
      :ptOptions="{ mergeSections: true, mergeProps: true }"
      :pt="btnPresent"
      :useFlat="useFlat"
      :rounded="rounded"
      :iconPos="iconPos"
      class="h-[40px] app-button"
      :class="[cssClass || '', { '!cursor-not-allowed': disabled }]"
      ref="buttonRef"
    >
      <template #icon v-if="iconImgComp">
        <div class="w-[20px] h-[20px]">
          <img :src="iconImgComp" />
        </div>
      </template>
      <template #icon v-if="icon">
        <div v-if="useIconify" :class="{ 'rotate-180': reverseIconDirection }">
          <Icon :icon="icon" />
        </div>
        <i :class="[icon, iconClass, { 'rotate-180': reverseIconDirection }]" v-else></i>
      </template>
      <slot></slot>
    </Button>
  </div>
</template>
<style>
.p-tooltip {
  max-width: 200px !important;
  word-wrap: break-word;
  white-space: normal;
}
</style>
